import React from 'react';

export interface BadgeProps {
  /** Badge color variant */
  variant?: 'accent' | 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'lavender' | 'yellow' | 'mint';
  /** Show dot indicator before text */
  dot?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Badge content */
  children: React.ReactNode;
}

/**
 * Canonical Badge component – replaces all 10+ inline badge patterns.
 *
 * All badges are pill-shaped with semi-transparent colored backgrounds
 * and matching text colors. Based on Dark Canvas design reference.
 */
export const Badge: React.FC<BadgeProps> = ({
  variant = 'neutral',
  dot = false,
  className = '',
  children,
}) => {
  const variantStyles: Record<string, string> = {
    accent: 'bg-accent-medium text-accent',
    success: 'bg-success-medium text-success',
    warning: 'bg-warning-soft text-warning',
    danger: 'bg-danger-medium text-danger',
    info: 'bg-info-soft text-info',
    neutral: 'bg-bg-surface text-txt-secondary border border-default',
    lavender: 'bg-pastel-lavender text-[#2E1065]',
    yellow: 'bg-pastel-yellow text-[#422006]',
    mint: 'bg-pastel-mint text-[#052E16]',
  };

  return (
    <span
      className={`
        inline-flex items-center gap-[5px]
        px-2.5 py-0.5
        rounded-badge
        text-[11.5px] font-semibold
        ${variantStyles[variant] || variantStyles.neutral}
        ${className}
      `.trim().replace(/\s+/g, ' ')}
    >
      {dot && (
        <span className="w-[5px] h-[5px] rounded-full bg-current" />
      )}
      {children}
    </span>
  );
};

export default Badge;
